// StorySystem — the narrative engine. It listens to gameplay events on the
// EventBus and fires authored "story beats" (see storyConfig.ts), de-duplicated
// once per playthrough via store.seenStoryBeats. Beats are presented through
// three channels: a cinematic modal (StoryBeatModal), the social feed, and a
// choice (the EventPopup, e.g. "el clásico"). The system carries NO economic
// effects of its own, so the deterministic system tests are unaffected.
//
// The OPENING arc is seeded directly at new-game (seedOpeningStory) rather than
// on GAME_STARTED, so it never re-fires when loading a save ("Continuar").

import { EventBus, GameEvents, type ConstructionCompletedPayload } from '../utils/EventBus'
import { useGameStore } from '@/store/gameStore'
import type { EventType } from '@/config/eventsConfig'
import {
  firstCourtBeat, firstSeasonBeat, rivalSeasonBeat, boardObjectiveBeat, boardVerdictBeat,
  clasicoBeat, openingStoryBeats, type StoryBeat, type StoryContext,
} from '@/config/storyConfig'

// The derby fires at the midpoint of a 14-matchday season.
const CLASICO_MATCHDAY = 7

/** Route a beat to its channel, once. Safe to call repeatedly (seen de-dup). */
export function presentStoryBeat(beat: StoryBeat | null): void {
  if (!beat) return
  const store = useGameStore.getState()
  if (store.seenStoryBeats.includes(beat.id)) return

  if (beat.presentation === 'choice' && beat.choice) {
    // Don't clobber an event already on screen — let a later trigger retry.
    if (store.pendingEvent) return
    store.markStoryBeatSeen(beat.id)
    store.setPendingEvent(beat.choice.eventType as EventType)
    return
  }

  store.markStoryBeatSeen(beat.id)
  if (beat.presentation === 'modal' && beat.modal) {
    store.enqueueStoryBeat(beat)
  } else if (beat.presentation === 'feed' && beat.feed) {
    store.pushStoryNews({
      id: beat.id,
      headline: beat.feed.headline,
      category: 'story',
      authorId: beat.feed.authorId,
    })
  }
}

/** Context snapshot from the current store (clubName falls back gracefully). */
function currentContext(payload?: unknown): StoryContext {
  const s = useGameStore.getState()
  return {
    region: s.region,
    clubName: s.managerClubName || 'tu club',
    gameMode: s.gameMode,
    day: s.day,
    payload,
  }
}

/** Seed the opening arc (called once from the new-game flow, not on Continuar). */
export function seedOpeningStory(ctx: StoryContext): void {
  openingStoryBeats(ctx).forEach(presentStoryBeat)
}

export class StorySystem {
  private onConstruction = (payload: ConstructionCompletedPayload) =>
    presentStoryBeat(firstCourtBeat(currentContext(payload)))

  private onSeasonEnd = (payload: unknown) => {
    const ctx = currentContext(payload)
    presentStoryBeat(firstSeasonBeat(ctx))      // once, cinematic
    presentStoryBeat(rivalSeasonBeat(ctx))      // per-season rival jab
  }

  private onObjectiveSet = (payload: unknown) =>
    presentStoryBeat(boardObjectiveBeat(currentContext(payload)))

  private onObjectiveMet = (payload: unknown) =>
    presentStoryBeat(boardVerdictBeat(currentContext(payload), true))

  private onObjectiveFailed = (payload: unknown) =>
    presentStoryBeat(boardVerdictBeat(currentContext(payload), false))

  // Mid-season derby: fire "el clásico" once at matchday 7 of an active league.
  private onWeek = () => {
    const s = useGameStore.getState()
    const league = s.league
    if (!league || league.phase === 'finished') return
    if (s.week - league.seasonStartWeek !== CLASICO_MATCHDAY) return
    presentStoryBeat(clasicoBeat(currentContext({ season: league.season })))
  }

  constructor() {
    EventBus.on(GameEvents.CONSTRUCTION_COMPLETED, this.onConstruction)
    EventBus.on(GameEvents.LEAGUE_SEASON_ENDED, this.onSeasonEnd)
    EventBus.on(GameEvents.BOARD_OBJECTIVE_SET, this.onObjectiveSet)
    EventBus.on(GameEvents.BOARD_OBJECTIVE_MET, this.onObjectiveMet)
    EventBus.on(GameEvents.BOARD_OBJECTIVE_FAILED, this.onObjectiveFailed)
    EventBus.on(GameEvents.WEEK_CHANGED, this.onWeek)
  }

  destroy() {
    EventBus.off(GameEvents.CONSTRUCTION_COMPLETED, this.onConstruction)
    EventBus.off(GameEvents.LEAGUE_SEASON_ENDED, this.onSeasonEnd)
    EventBus.off(GameEvents.BOARD_OBJECTIVE_SET, this.onObjectiveSet)
    EventBus.off(GameEvents.BOARD_OBJECTIVE_MET, this.onObjectiveMet)
    EventBus.off(GameEvents.BOARD_OBJECTIVE_FAILED, this.onObjectiveFailed)
    EventBus.off(GameEvents.WEEK_CHANGED, this.onWeek)
  }
}
